import styled from '@emotion/styled';
import { Button, Input, Modal, notification, Table } from 'antd';
import moment from 'moment';
import { useState } from 'react';
import { FaDollarSign } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { ConnectedUserEntity } from '../../../../../entities/ConnectedUserEntity';
import { TransactionEntity } from '../../../../../entities/transaction.entity';
import { PRIMARY } from '../../../../../shared/colors';
import { formatNum } from '../../../../../shared/constants';
import { finalizeTransaction } from '../network/network';

const MAX_VISIBLE_ACTIVITY_ROWS = 6;
const BUY_COLOR = '#008f4f';
const SELL_COLOR = '#d92d20';

const Container = styled.div`
  color: var(--mt-text);
  padding: 8px 10px 10px;
  margin-bottom: 5px;
  font-family: Poppins-R;
  height: 100%;
  max-height: none;
  overflow-x: auto;
  overflow-y: auto;

  th {
    background-color: ${PRIMARY} !important;
    color: white !important;
    font-family: Poppins-B;
    font-size: 11px;
    font-weight: normal;
    text-transform: uppercase;
  }

  .activity-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 6px;
    color: var(--mt-muted);
    font-size: 11px;
  }

  .activity-summary strong {
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .activity-summary span {
    padding: 3px 8px;
    border: 1px solid rgba(255, 176, 0, 0.35);
    border-radius: 999px;
    color: var(--mt-warning);
    font-family: Poppins-B;
  }

  .side-pill,
  .status-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 54px;
    padding: 2px 7px;
    border-radius: 999px;
    font-family: Poppins-B;
    font-size: 10px;
    text-transform: uppercase;
  }

  .side-pill.buy {
    color: ${BUY_COLOR};
    background-color: rgba(0, 143, 79, 0.1);
  }

  .side-pill.sell {
    color: ${SELL_COLOR};
    background-color: rgba(217, 45, 32, 0.1);
  }

  .status-pill {
    color: var(--mt-warning);
    background-color: rgba(255, 176, 0, 0.12);
    border: 1px solid rgba(255, 176, 0, 0.25);
  }

  .action-button {
    width: 26px;
    height: 26px;
    border-radius: 999px;
    background-color: var(--mt-surface) !important;
    border: 1px solid var(--mt-border);
  }

  .empty-state {
    display: grid;
    min-height: 110px;
    place-items: center;
    color: var(--mt-muted);
    border: 1px dashed var(--mt-border);
    border-radius: 8px;
    background-color: var(--mt-surface);
  }

  &::-webkit-scrollbar {
    width: 0.5vw;
  }

  &::-webkit-scrollbar-thumb {
    background-image: linear-gradient(to bottom, #eceff1, #eceff1);
    border-radius: 10px;
  }
`;

export const Transactions = ({ transactions }: { transactions: any[] }) => {
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTransact, setSelectedTransact] = useState<TransactionEntity>();
  const [numTelephone, setNumTelephone] = useState('+2376');
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;

  const finalize = () => {
    setIsLoading(true);

    let schema = '';
    let codeClient = selectedTransact?.detenteur.code_referent;
    if (connectedUser.partenaire.code_referent === codeClient) {
      schema = 'VENTCP';
    } else {
      schema = 'VENTCT';
    }

    const dataToCreate = {
      schema,
      userId: connectedUser.id,
      montant: Math.ceil(
        Number(selectedTransact?.qte) * Number(selectedTransact?.prixNominale) +
          Number(selectedTransact?.couponCouruPayer) +
          Number(selectedTransact?.commIntermediation) +
          Number(selectedTransact?.commConservation) +
          Number(selectedTransact?.commDroitGarde) +
          Number(selectedTransact?.commBrokerage),
      ),
      telephone: numTelephone,
    };

    finalizeTransaction(selectedTransact?.id as string, dataToCreate)
      .then(({ statusCode, data }) => {
        if (statusCode.toString()[0] === '2') {
          notification.info({
            message: 'Statut',
            description: data.message,
          });
          setShowModal(false);
        } else {
          notification.error({
            message: 'Erreur',
            description: data.message,
          });
        }
      })
      .finally(() => setIsLoading(false));
  };

  const COLUMNS = [
    {
      title: 'Pays',
      render: (cell: any, row: TransactionEntity) => row.titre.pays,
    },
    {
      title: 'CODE',
      render: (cell: any, row: TransactionEntity) => row.titre.code,
    },
    {
      title: 'Detenteur',
      render: (cell: any, row: TransactionEntity) =>
        row.detenteur.code_referent,
    },
    {
      title: 'Opération',
      dataIndex: 'typeOperation',
      render: (cell: string) => (
        <span className={`side-pill ${cell === 'ACHAT' ? 'buy' : 'sell'}`}>
          {cell}
        </span>
      ),
    },
    {
      title: 'Date Ope',
      dataIndex: 'dateOperation',
      render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
    },
    {
      title: 'Qte',
      dataIndex: 'qte',
    },
    {
      title: 'Coupon',
      dataIndex: 'tauxInteret',
    },
    {
      title: 'Décote',
      dataIndex: 'decote',
    },
    {
      title: 'Prix total',
      render: (cell: any, row: TransactionEntity) =>
        `${formatNum(row.qte * row.prixNominale)}`,
    },
    {
      title: 'Net',
      render: (cell: any, row: TransactionEntity) =>
        `${formatNum(
          Math.ceil(
            Number(row.qte) * Number(row.prixNominale) +
              Number(row.couponCouruPayer) +
              Number(row.commIntermediation) +
              Number(row.commConservation) +
              Number(row.commDroitGarde) +
              Number(row.commBrokerage),
          ),
        )}`,
    },
    {
      title: 'Statut',
      dataIndex: 'statut',
      render: (cell: string) => <span className='status-pill'>{cell}</span>,
    },
    {
      title: 'Action',
      render: (cell: any, row: TransactionEntity) => (
        <Button
          className='action-button'
          icon={
            <FaDollarSign
              style={{ color: 'var(--mt-text)', marginBottom: -2 }}
            />
          }
          style={{
            backgroundColor: 'transparent',
            zoom: 0.8,
          }}
          onClick={() => {
            setSelectedTransact(row);
            setShowModal(true);
          }}
          disabled={
            row.statut === 'finalisé'
              ? true
              : row.typeOperation === 'ACHAT'
              ? false
              : true
          }
        />
      ),
    },
  ];

  const visibleTransactions = transactions.slice(0, MAX_VISIBLE_ACTIVITY_ROWS);
  const hiddenTransactions = Math.max(
    transactions.length - visibleTransactions.length,
    0,
  );

  return (
    <Container>
      {transactions.length === 0 ? (
        <div className='empty-state'>Aucune transaction disponible</div>
      ) : (
        <>
          <div className='activity-summary'>
            <strong>Dernières transactions</strong>
            <span>
              {visibleTransactions.length}/{transactions.length} affichées
            </span>
          </div>
          <Table
            columns={COLUMNS}
            dataSource={visibleTransactions}
            rowClassName={(row, index) =>
              index % 2 === 0 ? 'even-row' : 'odd-row'
            }
            rowKey={(row: TransactionEntity) => row.id}
            size='small'
            scroll={{ x: 980 }}
            pagination={false}
          />
          {hiddenTransactions > 0 && (
            <div
              className='activity-summary'
              style={{ marginTop: 8, marginBottom: 0 }}
            >
              <span>{hiddenTransactions} transactions masquées</span>
              <strong>Vue complète disponible dans l'historique</strong>
            </div>
          )}
        </>
      )}

      <Modal
        className='dark-modal'
        open={showModal}
        title='Finaliser la transaction'
        onCancel={() => setShowModal(false)}
        footer={[
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              onClick={() => setShowModal(false)}
              style={{ borderRadius: 20 }}
            >
              Fermer
            </Button>
            <Button
              className='butt'
              loading={isLoading}
              style={{ borderRadius: 20 }}
              onClick={finalize}
            >
              Finaliser
            </Button>
          </div>,
        ]}
      >
        {selectedTransact && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <p>Montant de la transcation :</p>
              <p>
                Numéro de téléphone{' '}
                <i style={{ color: 'gray', fontSize: 12 }}>(OM/MoMo)</i> :
              </p>
            </div>
            <div>
              <p style={{ fontWeight: 'bold', color: 'green' }}>
                {formatNum(
                  Math.ceil(
                    Number(selectedTransact.qte) *
                      Number(selectedTransact.prixNominale) +
                      Number(selectedTransact.couponCouruPayer) +
                      Number(selectedTransact.commIntermediation) +
                      Number(selectedTransact.commConservation) +
                      Number(selectedTransact.commDroitGarde) +
                      Number(selectedTransact.commBrokerage),
                  ),
                )}{' '}
                XAF
              </p>
              <Input
                placeholder='numéro téléphone'
                value={numTelephone}
                onChange={(e) => setNumTelephone(e.target.value)}
              />
            </div>
          </div>
        )}
      </Modal>
    </Container>
  );
};
